import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogOpenChangeData,
  DialogOpenChangeEvent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { Dismiss24Regular, FullScreenMaximize24Regular, FullScreenMinimize24Regular } from "@fluentui/react-icons";
import * as React from "react";
import { memo, ReactNode, useCallback, useMemo, useState } from "react";
import { useStyles } from "./Modal.styles";

type ModalButtonProps = Omit<ButtonProps, "onClick">;

interface ModalProps {
  open: boolean;
  title?: string | ReactNode;
  content?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
  modalType?: "alert" | "modal" | "non-modal";
  confirmButtonProps?: ModalButtonProps;
  cancelButtonProps?: ModalButtonProps;
  customActions?: ReactNode;
  hideCancel?: boolean;
  hideConfirm?: boolean;
  showDismiss?: boolean;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

/**
 * Unified Modal Component
 *
 * @param props - Modal configuration props
 * @param props.open - Whether the modal is open
 * @param props.title - Modal title
 * @param props.content - Modal content
 * @param props.confirmText - Text for confirm button (default: "Confirm")
 * @param props.cancelText - Text for cancel button (default: "Cancel")
 * @param props.onConfirm - Callback when confirm is clicked (can be async)
 * @param props.onCancel - Callback when cancel is clicked
 * @param props.onClose - Callback when modal is closed (ESC or dismiss button)
 * @param props.modalType - FluentUI modal type: "alert" | "modal" (default: "modal")
 * @param props.confirmButtonProps - Additional props for confirm button
 * @param props.cancelButtonProps - Additional props for cancel button
 * @param props.customActions - Custom action buttons (replaces default buttons)
 * @param props.hideCancel - Hide cancel button
 * @param props.hideConfirm - Hide confirm button
 * @param props.showDismiss - Show dismiss button in title (default: true when onClose or onCancel is provided, false otherwise)
 * @param props.fullscreen - Whether to use fullscreen mode
 * @param props.onToggleFullscreen - Callback when fullscreen is toggled
 */
export const AppModal: React.FC<ModalProps> = (props) => {
  const {
    open,
    title,
    content,
    confirmText = "Confirm",
    cancelText = "Cancel",
    onConfirm,
    onCancel,
    onClose,
    modalType = "modal",
    confirmButtonProps = {},
    cancelButtonProps = {},
    customActions,
    hideCancel = false,
    hideConfirm = false,
    showDismiss = !!(onClose || onCancel),
    fullscreen = false,
    onToggleFullscreen,
  } = props;

  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!onConfirm) return;

    try {
      setIsLoading(true);
      const result = onConfirm();

      // Handle async functions
      if (result instanceof Promise) {
        await result;
      }
    } catch (error) {
      console.error("Modal onConfirm error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (onCancel) {
      onCancel();
    }
  }, [onClose, onCancel]);

  const handleOpenChange = useCallback(
    (_: DialogOpenChangeEvent, data: DialogOpenChangeData) => {
      if (data.open) return;

      if (fullscreen && onToggleFullscreen) {
        // While fullscreen, Esc/backdrop restores rather than closing the modal.
        onToggleFullscreen();
        return;
      }
      handleClose();
    },
    [fullscreen, onToggleFullscreen, handleClose]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modalType={modalType}>
      <DialogSurface
        className={fullscreen ? styles.fullscreenSurface : undefined}
        data-fullscreen={fullscreen ? "true" : undefined}
      >
        <DialogBody>
          <Title
            title={title}
            onClose={showDismiss ? handleClose : undefined}
            fullscreen={fullscreen}
            onToggleFullscreen={onToggleFullscreen}
            titleActionsClassName={styles.titleActions}
          />
          <DialogContent>
            <Content content={content} />
          </DialogContent>
          <DialogActions>
            <Actions
              customActions={customActions}
              hideCancel={hideCancel}
              hideConfirm={hideConfirm}
              cancelText={cancelText}
              confirmText={confirmText}
              isLoading={isLoading}
              onCancel={handleCancel}
              onConfirm={handleConfirm}
              cancelButtonProps={cancelButtonProps}
              confirmButtonProps={confirmButtonProps}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

// Internal Title component
interface TitleProps {
  title?: string | ReactNode;
  onClose?: () => void;
  fullscreen: boolean;
  onToggleFullscreen?: () => void;
  titleActionsClassName: string;
}

const Title = memo<TitleProps>(function Title({
  title,
  onClose,
  fullscreen,
  onToggleFullscreen,
  titleActionsClassName,
}: TitleProps) {
  if (!onToggleFullscreen && !onClose) {
    return <DialogTitle>{title}</DialogTitle>;
  }

  return (
    <DialogTitle
      action={
        <div className={titleActionsClassName}>
          {onToggleFullscreen && <FullscreenToggleButton fullscreen={fullscreen} onClick={onToggleFullscreen} />}
          {onClose && <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} onClick={onClose} />}
        </div>
      }
    >
      {title}
    </DialogTitle>
  );
});

interface FullscreenToggleButtonProps {
  fullscreen: boolean;
  onClick: () => void;
}

/** Maximize/restore button whose label and icon follow the current fullscreen state. */
const FullscreenToggleButton = ({ fullscreen, onClick }: FullscreenToggleButtonProps) => {
  const { label, icon } = useMemo(() => {
    if (fullscreen) {
      return { label: "Exit fullscreen", icon: <FullScreenMinimize24Regular /> };
    }
    return { label: "Enter fullscreen", icon: <FullScreenMaximize24Regular /> };
  }, [fullscreen]);

  return <Button appearance="subtle" aria-label={label} icon={icon} onClick={onClick} />;
};

// Internal Content component
interface ContentProps {
  content?: string | ReactNode;
}

const Content = memo<ContentProps>(function Content({ content }: ContentProps) {
  if (typeof content === "string") {
    return <Text>{content}</Text>;
  }
  return <>{content}</>;
});

// Internal Actions component
interface ActionsProps {
  customActions?: ReactNode;
  hideCancel: boolean;
  hideConfirm: boolean;
  cancelText: string;
  confirmText: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  cancelButtonProps: Record<string, any>;
  confirmButtonProps: Record<string, any>;
}

const Actions = memo<ActionsProps>(function Actions({
  customActions,
  hideCancel,
  hideConfirm,
  cancelText,
  confirmText,
  isLoading,
  onCancel,
  onConfirm,
  cancelButtonProps,
  confirmButtonProps,
}: ActionsProps) {
  if (customActions) {
    return <>{customActions}</>;
  }

  return (
    <>
      {!hideCancel && (
        <Button appearance="secondary" onClick={onCancel} disabled={isLoading} {...cancelButtonProps}>
          {cancelText}
        </Button>
      )}
      {!hideConfirm && (
        <Button appearance="primary" onClick={onConfirm} disabled={isLoading} {...confirmButtonProps}>
          {isLoading ? <Spinner size="tiny" /> : confirmText}
        </Button>
      )}
    </>
  );
});
